import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import PersonIcon from "@mui/icons-material/PersonOutlined";
import { useEffect, useMemo, useState, type MouseEvent } from "react";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";
import {
  ADMIN_ONLY_PERMISSIONS,
  DEFAULT_PERMISSIONS_FOR_ROLE,
  PERMISSION_CATALOGUE,
  SIMPLE_ROLES,
  storedRoleToSimple,
  type PermissionKey,
  type SimpleRole,
} from "@shared/types/auth";
import {
  CONTENT_OWNER_KIND_OPTIONS,
  foyerApi,
  type CompanySummary,
  type ContentOwnerKindValue,
  type ContentOwnerRecord,
  type ContentSupplierRow,
  type InviteRecord,
  type PremiseSummary,
  type UserRecord,
} from "../http/foyer";
import { errorCode, friendlyError } from "../utils/apiError";

/** Lightweight email shape check for inline form validation. */
function isEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

/**
 * A live preview only, same caveat as `ContentProvidersPage`'s copy: a
 * runtime import of `@shared/content-owner` is not safe in the standalone
 * build that actually ships (`global/pitfalls/shared-alias-build-lanes.md`),
 * so this stays a local mirror rather than a shared import.
 */
function slugPreview(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Give a content provider its login as part of creating/updating the user it
 * is for, rather than as a step of its own (`[[mrss-feed-content-providers]]`,
 * ruled 2026-09-04 and revised the same day: a login with nobody to use it
 * is not a step forward). A no-op when the chosen provider already has one.
 *
 * Throws the raw error on failure — callers decide how to surface it
 * (a field, a toast) rather than this function picking for them.
 */
async function ensureOwnerLogin(params: {
  supplierId: string;
  suppliers: ContentSupplierRow[];
  slug: string;
  contactEmail: string;
  /** `CONTENT-23`: required whenever a login is actually created. */
  kind: ContentOwnerKindValue | "";
}): Promise<void> {
  const supplier = params.suppliers.find((s) => s.id === params.supplierId);
  if (!supplier || supplier.hasLogin) return;
  if (!params.kind) {
    // Same shape as a server refusal, so the caller's hint handling applies.
    throw { body: { hint: "Pick what kind of organisation this is." } };
  }
  await foyerApi.createContentOwner({
    supplierId: params.supplierId,
    name: supplier.name,
    kind: params.kind,
    slug: params.slug.trim() || undefined,
    contactEmail: params.contactEmail.trim() || undefined,
  });
}

/** The hint a `content-owners` 409/400 carries, falling back to the generic message. */
function ownerLoginErrorHint(e: unknown): string {
  const err = e as { body?: { hint?: string } };
  return err.body?.hint ?? friendlyError(e);
}

interface ToastState {
  msg: string;
  severity: "success" | "error";
}

export default function UsersPage() {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [invites, setInvites] = useState<InviteRecord[]>([]);
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [premises, setPremises] = useState<PremiseSummary[]>([]);
  const [owners, setOwners] = useState<ContentOwnerRecord[]>([]);
  const [suppliers, setSuppliers] = useState<ContentSupplierRow[]>([]);
  /** The list could not be read - which is NOT the same as it being empty. */
  const [suppliersUnavailable, setSuppliersUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<UserRecord | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuUser, setMenuUser] = useState<UserRecord | null>(null);

  function openRowMenu(e: MouseEvent<HTMLElement>, u: UserRecord) {
    setMenuAnchor(e.currentTarget);
    setMenuUser(u);
  }
  function closeRowMenu() {
    setMenuAnchor(null);
    setMenuUser(null);
  }

  async function refresh() {
    setLoading(true);
    try {
      const [u, i, c, p, o, s] = await Promise.all([
        foyerApi.listUsers(),
        foyerApi.listInvites(),
        foyerApi.listCompanies(),
        foyerApi.listPremises(),
        // Only used here for the "(disabled)" check on a login that already
        // exists - a failure just means that check finds nothing, so it
        // degrades to an empty list rather than needing its own failed state.
        foyerApi.listContentOwners().catch(() => [] as ContentOwnerRecord[]),
        // The picker offers every content PROVIDER, not just the ones that
        // already have a login - picking a loginless one is how a login gets
        // created at all now (`[[mrss-feed-content-providers]]`).
        //
        // 🚨 It degrades to a FAILURE, not to an empty list. "Nobody has been
        // added yet" and "we could not ask" render identically and mean
        // opposite things, and the operator acts on the first one - which is
        // how the picker came to tell people to go and create something that
        // may well already exist.
        foyerApi.listContentSuppliers().then(
          (res) => ({ rows: res.suppliers, failed: false }),
          () => ({ rows: [] as ContentSupplierRow[], failed: true })
        ),
      ]);
      setUsers(u);
      setInvites(i);
      setCompanies(c);
      setPremises(p);
      setOwners(o);
      setSuppliers(s.rows);
      setSuppliersUnavailable(s.failed);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  const companyName = useMemo(
    () => new Map(companies.map((c) => [c.id, c.name])),
    [companies]
  );
  const premiseName = useMemo(
    () =>
      new Map(
        premises.map((p) => [p.premiseId, p.premiseName]) as [string, string][]
      ),
    [premises]
  );
  const ownerName = useMemo(
    () => new Map(owners.map((o) => [o.id, o.name])),
    [owners]
  );

  async function revokeInvite(id: string) {
    if (!confirm("Revoke this invite?")) return;
    try {
      await foyerApi.revokeInvite(id);
      setToast({ msg: "Invite revoked", severity: "success" });
      refresh();
    } catch (e) {
      setToast({
        msg: e instanceof Error ? e.message : String(e),
        severity: "error",
      });
    }
  }

  async function deleteUser(u: UserRecord) {
    if (!confirm(`Delete ${u.email}?`)) return;
    try {
      await foyerApi.deleteUser(u.id);
      setToast({ msg: "User deleted", severity: "success" });
      refresh();
    } catch (e) {
      setToast({
        msg: e instanceof Error ? e.message : String(e),
        severity: "error",
      });
    }
  }

  function copyToClipboard(text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => setToast({ msg: "Link copied", severity: "success" }))
      .catch(() => setToast({ msg: "Could not copy link", severity: "error" }));
  }

  if (loading && users.length === 0) {
    return (
      <PageContainer>
        <PageHeader eyebrow="Access" title="Users" />
        <TableSkeleton rows={6} />
      </PageContainer>
    );
  }

  if (err && users.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{err}</Alert>
      </Box>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Access"
        title="Users"
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<PersonIcon />}
              onClick={() => setAddOpen(true)}
              data-testid="users-add"
            >
              Add user
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PersonAddIcon />}
              onClick={() => setInviteOpen(true)}
              data-testid="users-invite"
            >
              Invite user
            </Button>
          </>
        }
      />

      <Card sx={{ overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 320px)" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell sx={{ width: 120 }}>Role</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell sx={{ width: 220 }}>Scope</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box
                      sx={{
                        py: 5,
                        textAlign: "center",
                        color: "text.secondary",
                      }}
                    >
                      <Typography variant="body2">No users yet.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {users.map((u) => {
                const role = storedRoleToSimple(u.type);
                const roleLabel =
                  SIMPLE_ROLES.find((r) => r.key === role)?.label ?? u.type;
                return (
                  <TableRow key={u.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {u.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={roleLabel}
                        color={role === "admin" ? "primary" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      {u.type === "admin" ? (
                        <Typography variant="caption" color="text.secondary">
                          All (admin)
                        </Typography>
                      ) : u.permissions.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      ) : (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {u.permissions.map((p) => (
                            <Chip
                              key={p}
                              label={
                                PERMISSION_CATALOGUE.find((m) => m.key === p)
                                  ?.label ?? p
                              }
                              size="small"
                              variant="outlined"
                              sx={{ mb: 0.5 }}
                            />
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        {u.contentOwner && (
                          <Typography variant="caption">
                            Content provider:{" "}
                            <strong>
                              {ownerName.get(u.contentOwner) ?? u.contentOwner}
                            </strong>
                          </Typography>
                        )}
                        {u.company && (
                          <Typography variant="caption">
                            Company:{" "}
                            <strong>
                              {companyName.get(u.company) ?? u.company}
                            </strong>
                          </Typography>
                        )}
                        {u.premises.length > 0 && (
                          <Typography variant="caption">
                            + {u.premises.length} extra premise
                            {u.premises.length === 1 ? "" : "s"}
                          </Typography>
                        )}
                        {!u.contentOwner &&
                          !u.company &&
                          u.premises.length === 0 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Global
                            </Typography>
                          )}
                      </Stack>
                    </TableCell>
                    <TableCell padding="checkbox">
                      <IconButton
                        size="small"
                        aria-label={`Actions for ${u.email}`}
                        onClick={(e) => openRowMenu(e, u)}
                        data-testid={`user-menu-${u.email}`}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            if (menuUser) setEditing(menuUser);
            closeRowMenu();
          }}
          data-testid={menuUser ? `user-edit-${menuUser.email}` : undefined}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const u = menuUser;
            closeRowMenu();
            if (u) deleteUser(u);
          }}
          data-testid={menuUser ? `user-delete-${menuUser.email}` : undefined}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <Typography variant="h6">Pending invites</Typography>
      <Card sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell sx={{ width: 100 }}>Role</TableCell>
                <TableCell sx={{ width: 120 }}>Status</TableCell>
                <TableCell>Accept link</TableCell>
                <TableCell sx={{ width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {invites.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="caption" color="text.secondary">
                      No invites yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {invites.map((i) => {
                const role = storedRoleToSimple(i.type);
                const roleLabel =
                  SIMPLE_ROLES.find((r) => r.key === role)?.label ?? i.type;
                return (
                  <TableRow key={i.id} hover>
                    <TableCell>{i.email}</TableCell>
                    <TableCell>
                      <Chip size="small" label={roleLabel} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={i.status}
                        color={
                          i.status === "pending"
                            ? "warning"
                            : i.status === "redeemed"
                              ? "success"
                              : "default"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily:
                              'ui-monospace, "SF Mono", Menlo, monospace',
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 380,
                          }}
                        >
                          {i.acceptUrl}
                        </Typography>
                        {i.status === "pending" && (
                          <Tooltip title="Copy invite link">
                            <IconButton
                              size="small"
                              aria-label={`Copy invite link for ${i.email}`}
                              onClick={() => copyToClipboard(i.acceptUrl)}
                              data-testid={`invite-copy-${i.email}`}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Revoke invite">
                        <IconButton
                          size="small"
                          aria-label={`Revoke invite for ${i.email}`}
                          onClick={() => revokeInvite(i.id)}
                          data-testid={`invite-revoke-${i.email}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <InviteDialog
        open={inviteOpen}
        companies={companies}
        premises={premises}
        owners={owners}
        suppliers={suppliers}
        suppliersUnavailable={suppliersUnavailable}
        onClose={() => setInviteOpen(false)}
        onCreated={(created) => {
          setInviteOpen(false);
          setToast({
            msg: `Invite created for ${created.email}`,
            severity: "success",
          });
          refresh();
        }}
        onError={(msg) => setToast({ msg, severity: "error" })}
      />

      <AddUserDialog
        open={addOpen}
        companies={companies}
        premises={premises}
        owners={owners}
        suppliers={suppliers}
        suppliersUnavailable={suppliersUnavailable}
        onClose={() => setAddOpen(false)}
        onCreated={(created) => {
          setAddOpen(false);
          setToast({
            msg: `User ${created.email} created`,
            severity: "success",
          });
          refresh();
        }}
        onError={(msg) => setToast({ msg, severity: "error" })}
      />

      <UserEditDialog
        user={editing}
        companies={companies}
        premises={premises}
        owners={owners}
        suppliers={suppliers}
        suppliersUnavailable={suppliersUnavailable}
        premiseName={premiseName}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          setToast({ msg: "User updated", severity: "success" });
          refresh();
        }}
        onError={(msg) => setToast({ msg, severity: "error" })}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {toast ? (
          <Alert severity={toast.severity}>{toast.msg}</Alert>
        ) : undefined}
      </Snackbar>
    </PageContainer>
  );
}

interface InviteDialogProps {
  open: boolean;
  companies: CompanySummary[];
  premises: PremiseSummary[];
  owners: ContentOwnerRecord[];
  /** The owner list could not be READ - not the same as empty. */
  suppliers: ContentSupplierRow[];
  /** The supplier list could not be READ - not the same as empty. */
  suppliersUnavailable: boolean;
  onClose: () => void;
  onCreated: (i: InviteRecord) => void;
  onError: (msg: string) => void;
}

function InviteDialog({
  open,
  companies,
  premises,
  owners,
  suppliers,
  suppliersUnavailable,
  onClose,
  onCreated,
  onError,
}: InviteDialogProps) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<SimpleRole>("editor");
  const [company, setCompany] = useState<string>("");
  const [contentOwner, setContentOwner] = useState<string>("");
  const [extras, setExtras] = useState<string[]>([]);
  const [newLoginSlug, setNewLoginSlug] = useState("");
  const [newLoginContactEmail, setNewLoginContactEmail] = useState("");
  const [newLoginKind, setNewLoginKind] = useState<ContentOwnerKindValue | "">(
    ""
  );
  const [perms, setPerms] = useState<PermissionKey[]>(
    DEFAULT_PERMISSIONS_FOR_ROLE.editor
  );
  const [busy, setBusy] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail("");
      setRole("editor");
      setCompany("");
      setContentOwner("");
      setExtras([]);
      setNewLoginSlug("");
      setNewLoginContactEmail("");
      setNewLoginKind("");
      setPerms(DEFAULT_PERMISSIONS_FOR_ROLE.editor);
      setEmailError(null);
    }
  }, [open]);

  function changeRole(next: SimpleRole) {
    setRole(next);
    setPerms(DEFAULT_PERMISSIONS_FOR_ROLE[next]);
  }

  async function submit() {
    if (!isEmail(email)) {
      setEmailError("Enter a valid email address.");
      return;
    }
    setBusy(true);
    setEmailError(null);
    if (role === "contentOwner") {
      try {
        await ensureOwnerLogin({
          supplierId: contentOwner,
          suppliers,
          slug: newLoginSlug,
          contactEmail: newLoginContactEmail,
          kind: newLoginKind,
        });
      } catch (e) {
        onError(ownerLoginErrorHint(e));
        setBusy(false);
        return;
      }
    }
    try {
      const stored =
        SIMPLE_ROLES.find((r) => r.key === role)?.storedRole ?? "companyAdmin";
      const created = await foyerApi.createInvite({
        email,
        type: stored,
        permissions: grantablePermissions(perms, role, company, extras),
        // A content owner belongs to no company and holds no premise; sending
        // the other scope's fields anyway is what the server refuses.
        company: hasNoTenantScope(role) ? null : company || null,
        contentOwner: role === "contentOwner" ? contentOwner : null,
        premises: hasNoTenantScope(role) ? [] : extras,
      });
      onCreated(created);
    } catch (e) {
      const code = errorCode(e);
      if (code === "user_already_exists" || code === "invite_already_pending") {
        setEmailError(friendlyError(e));
      } else {
        onError(friendlyError(e));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite a new user</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError(null);
            }}
            fullWidth
            required
            size="small"
            autoFocus
            error={!!emailError}
            helperText={emailError ?? undefined}
            inputProps={{ "data-testid": "invite-email" }}
          />
          <TextField
            select
            label="Role"
            value={role}
            onChange={(e) => changeRole(e.target.value as SimpleRole)}
            size="small"
            inputProps={{ "data-testid": "invite-role" }}
          >
            {SIMPLE_ROLES.map((r) => (
              <MenuItem key={r.key} value={r.key}>
                <Box>
                  <Typography variant="body2">{r.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {r.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          <ScopePicker
            companies={companies}
            premises={premises}
            owners={owners}
            suppliers={suppliers}
            suppliersUnavailable={suppliersUnavailable}
            company={company}
            contentOwner={contentOwner}
            extras={extras}
            newLoginSlug={newLoginSlug}
            newLoginContactEmail={newLoginContactEmail}
            newLoginKind={newLoginKind}
            role={role}
            onCompany={setCompany}
            onContentOwner={setContentOwner}
            onExtras={setExtras}
            onNewLoginSlug={setNewLoginSlug}
            onNewLoginContactEmail={setNewLoginContactEmail}
            onNewLoginKind={setNewLoginKind}
          />

          <PermissionPicker
            value={perms}
            onChange={setPerms}
            disabled={role === "admin"}
            role={role}
            scoped={isCustomerScope(role, company, extras)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={busy || !isEmail(email)}
          onClick={submit}
          data-testid="invite-submit"
        >
          {busy ? "Sending…" : "Create invite"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface UserEditDialogProps {
  user: UserRecord | null;
  companies: CompanySummary[];
  premises: PremiseSummary[];
  owners: ContentOwnerRecord[];
  /** The owner list could not be READ - not the same as empty. */
  suppliers: ContentSupplierRow[];
  /** The supplier list could not be READ - not the same as empty. */
  suppliersUnavailable: boolean;
  premiseName: Map<string, string>;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}

function UserEditDialog({
  user,
  companies,
  premises,
  owners,
  suppliers,
  suppliersUnavailable,
  onClose,
  onSaved,
  onError,
}: UserEditDialogProps) {
  const [role, setRole] = useState<SimpleRole>("editor");
  const [perms, setPerms] = useState<PermissionKey[]>([]);
  const [company, setCompany] = useState<string>("");
  const [contentOwner, setContentOwner] = useState<string>("");
  const [extras, setExtras] = useState<string[]>([]);
  const [newLoginSlug, setNewLoginSlug] = useState("");
  const [newLoginContactEmail, setNewLoginContactEmail] = useState("");
  const [newLoginKind, setNewLoginKind] = useState<ContentOwnerKindValue | "">(
    ""
  );
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    const r = storedRoleToSimple(user.type);
    setRole(r);
    setPerms(user.permissions as PermissionKey[]);
    setCompany(user.company ?? "");
    setContentOwner(user.contentOwner ?? "");
    setExtras(user.premises);
    setNewLoginSlug("");
    setNewLoginContactEmail("");
    setNewLoginKind("");
    setPassword("");
  }, [user]);

  if (!user) return null;

  function changeRole(next: SimpleRole) {
    setRole(next);
    setPerms(DEFAULT_PERMISSIONS_FOR_ROLE[next]);
  }

  async function submit() {
    if (!user) return;
    setBusy(true);
    if (role === "contentOwner") {
      try {
        await ensureOwnerLogin({
          supplierId: contentOwner,
          suppliers,
          slug: newLoginSlug,
          contactEmail: newLoginContactEmail,
          kind: newLoginKind,
        });
      } catch (e) {
        onError(ownerLoginErrorHint(e));
        setBusy(false);
        return;
      }
    }
    try {
      const stored =
        SIMPLE_ROLES.find((r) => r.key === role)?.storedRole ?? "companyAdmin";
      await foyerApi.updateUser(user.id, {
        type: stored,
        permissions: grantablePermissions(perms, role, company, extras),
        company: hasNoTenantScope(role) ? null : company || null,
        contentOwner: role === "contentOwner" ? contentOwner : null,
        premises: hasNoTenantScope(role) ? [] : extras,
        ...(password ? { password } : {}),
      });
      onSaved();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit {user.email}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="Role"
            value={role}
            onChange={(e) => changeRole(e.target.value as SimpleRole)}
            size="small"
          >
            {SIMPLE_ROLES.map((r) => (
              <MenuItem key={r.key} value={r.key}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>

          <ScopePicker
            companies={companies}
            premises={premises}
            owners={owners}
            suppliers={suppliers}
            suppliersUnavailable={suppliersUnavailable}
            company={company}
            contentOwner={contentOwner}
            extras={extras}
            newLoginSlug={newLoginSlug}
            newLoginContactEmail={newLoginContactEmail}
            newLoginKind={newLoginKind}
            role={role}
            onCompany={setCompany}
            onContentOwner={setContentOwner}
            onExtras={setExtras}
            onNewLoginSlug={setNewLoginSlug}
            onNewLoginContactEmail={setNewLoginContactEmail}
            onNewLoginKind={setNewLoginKind}
          />

          <PermissionPicker
            value={perms}
            onChange={setPerms}
            disabled={role === "admin"}
            role={role}
            scoped={isCustomerScope(role, company, extras)}
          />

          <TextField
            label="Reset password (optional)"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            size="small"
            helperText="Leave blank to keep the existing password."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={submit}
          data-testid="user-save"
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Who a user is scoped to.
 *
 * A content owner is NOT a narrower company user: they belong to no customer
 * and hold no location, so the picker swaps rather than adds. Showing all three
 * at once would invite the combination the server refuses - a supplier with a
 * premise attached - and a form that offers an invalid state is a form people
 * fill in.
 *
 * ## Picking a provider with no login yet IS how it gets one
 *
 * Ruled by Sigge 2026-09-04, reversed and re-ruled the same day
 * (`[[mrss-feed-content-providers]]`): a "give login" button on the Content
 * providers page created a login with nobody to use it - not a step forward.
 * The picker here lists every content PROVIDER, not just the ones that
 * already have a login, and choosing a loginless one reveals the two fields
 * a login needs (handle, contact email). Creating the user then creates the
 * login first, at that provider's own id - see `ensureOwnerLogin`.
 */
/**
 * Roles that belong to no company and hold no location. A content owner's scope
 * is their own catalogue; a Nenda admin already reaches every customer, and the
 * server refuses to store either with a tenant scope (`admin_has_no_tenant_scope`).
 */
function hasNoTenantScope(role: SimpleRole): boolean {
  return role === "contentOwner" || role === "admin";
}

/**
 * A staff role (Company admin / Viewer) that is tied to a company or to
 * locations - i.e. a customer's user, not Nenda staff. Such a user can never
 * hold a staff-only key: the server refuses it (`staff_only_permission`) and
 * ignores it at request time (`isTenantScoped`).
 */
function isCustomerScope(
  role: SimpleRole,
  company: string,
  extras: string[]
): boolean {
  return !hasNoTenantScope(role) && (!!company || extras.length > 0);
}

/**
 * The permissions to send: the picker's selection, minus staff-only keys when
 * the user is a customer's. The switches are hidden in that case, so a key
 * picked before a company was chosen would otherwise be sent invisibly - and
 * refused.
 */
function grantablePermissions(
  perms: PermissionKey[],
  role: SimpleRole,
  company: string,
  extras: string[]
): PermissionKey[] {
  return isCustomerScope(role, company, extras)
    ? perms.filter((k) => !ADMIN_ONLY_PERMISSIONS.has(k))
    : perms;
}

function ScopePicker({
  companies,
  premises,
  owners,
  suppliers,
  suppliersUnavailable,
  company,
  contentOwner,
  extras,
  newLoginSlug,
  newLoginContactEmail,
  newLoginKind,
  role,
  onCompany,
  onContentOwner,
  onExtras,
  onNewLoginSlug,
  onNewLoginContactEmail,
  onNewLoginKind,
}: {
  companies: CompanySummary[];
  premises: PremiseSummary[];
  owners: ContentOwnerRecord[];
  /** The owner list could not be READ - not the same as empty. */
  suppliers: ContentSupplierRow[];
  /** The supplier list could not be READ - not the same as empty. */
  suppliersUnavailable: boolean;
  company: string;
  contentOwner: string;
  extras: string[];
  /** Only meaningful while the chosen provider has no login yet. */
  newLoginSlug: string;
  newLoginContactEmail: string;
  newLoginKind: ContentOwnerKindValue | "";
  role: SimpleRole;
  onCompany: (id: string) => void;
  onContentOwner: (id: string) => void;
  onExtras: (ids: string[]) => void;
  onNewLoginSlug: (v: string) => void;
  onNewLoginContactEmail: (v: string) => void;
  onNewLoginKind: (v: ContentOwnerKindValue | "") => void;
}) {
  if (role === "admin") {
    return (
      <Alert severity="info" sx={{ py: 0.5 }} data-testid="scope-nenda-admin">
        A Nenda admin belongs to no company and reaches every customer. For a
        customer&apos;s own administrator, choose <strong>Company admin</strong>
        .
      </Alert>
    );
  }
  if (role === "contentOwner") {
    const selected = suppliers.find((s) => s.id === contentOwner);
    const needsLogin = selected != null && !selected.hasLogin;
    return (
      <Stack spacing={2}>
        <TextField
          select
          label="Content provider"
          value={contentOwner}
          onChange={(e) => {
            const id = e.target.value;
            onContentOwner(id);
            const next = suppliers.find((s) => s.id === id);
            // A fresh pick starts the login fields fresh too - stale text
            // from a previous, different provider must not travel silently.
            onNewLoginSlug(
              next && !next.hasLogin ? slugPreview(next.name) : ""
            );
            onNewLoginContactEmail("");
            onNewLoginKind("");
          }}
          size="small"
          required
          error={contentOwner === ""}
          helperText={
            // 🚨 Three states, not two. Naming the exact screen matters: this
            // text used to say "under Distribution", and the operator who went
            // looking found six sections and no way to create one.
            suppliersUnavailable
              ? "The content-provider list could not be read, so this picker may be incomplete. Reload before deciding nothing is there."
              : suppliers.length === 0
                ? "No content providers yet - add one under Distribution → Content providers, then come back."
                : needsLogin
                  ? "This provider has no login yet - fill in the fields below to create one."
                  : "The organisation this login speaks for. They see only its titles."
          }
          inputProps={{ "data-testid": "scope-content-owner" }}
        >
          {suppliers.map((s) => {
            const owner = owners.find((o) => o.id === s.id);
            const disabled = owner ? !owner.enabled : false;
            return (
              <MenuItem key={s.id} value={s.id} disabled={disabled}>
                {s.name}
                {disabled
                  ? " (disabled)"
                  : !s.hasLogin
                    ? " (no login yet)"
                    : ""}
              </MenuItem>
            );
          })}
        </TextField>

        {needsLogin ? (
          <>
            <Alert severity="info" sx={{ py: 0.5 }}>
              <strong>{selected!.name}</strong> has no login yet - creating this
              user gives it one, tied to this provider.
            </Alert>
            {/* CONTENT-23: every owner is created knowing what it is. */}
            <TextField
              select
              label="Kind of organisation"
              value={newLoginKind}
              onChange={(e) =>
                onNewLoginKind(e.target.value as ContentOwnerKindValue)
              }
              size="small"
              required
              error={newLoginKind === ""}
              helperText="Used to filter the review queue, and for terms that differ per type."
              inputProps={{ "data-testid": "new-login-kind" }}
            >
              {CONTENT_OWNER_KIND_OPTIONS.map((k) => (
                <MenuItem key={k.value} value={k.value}>
                  {k.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Handle"
              value={newLoginSlug}
              onChange={(e) => onNewLoginSlug(e.target.value)}
              size="small"
              helperText="Their storefront is reached at /<handle>/login/… - filled in from the name, edit to override."
              inputProps={{ "data-testid": "new-login-slug" }}
            />
            <TextField
              label="Contact email"
              value={newLoginContactEmail}
              onChange={(e) => onNewLoginContactEmail(e.target.value)}
              size="small"
              helperText="Who to reach about their material. This is NOT the login itself - that is the user being created here."
              inputProps={{ "data-testid": "new-login-contact" }}
            />
          </>
        ) : null}
      </Stack>
    );
  }
  return (
    <Stack spacing={1}>
      <TextField
        select
        label="Home company"
        value={company}
        onChange={(e) => onCompany(e.target.value)}
        size="small"
      >
        <MenuItem value="">
          <em>None (global)</em>
        </MenuItem>
        {companies.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {c.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        SelectProps={{
          multiple: true,
          renderValue: (val) => {
            const list = val as string[];
            if (list.length === 0) return "None";
            return `${list.length} premise${list.length === 1 ? "" : "s"}`;
          },
        }}
        label="Extra premises"
        value={extras}
        onChange={(e) =>
          onExtras(
            typeof e.target.value === "string"
              ? e.target.value.split(",")
              : (e.target.value as string[])
          )
        }
        size="small"
        helperText="Additional premises accessible outside the home company."
      >
        {premises
          .filter((p) => !!p.mongoId)
          .map((p) => (
            <MenuItem key={p.mongoId!} value={p.mongoId!}>
              {p.premiseName}
            </MenuItem>
          ))}
      </TextField>
    </Stack>
  );
}

/**
 * The permission switches, filtered to the audience the role belongs to.
 *
 * ⚠️ `adminOnly` used to be documentation: the picker offered every key to
 * every role, so a content owner could be handed `canManageDistribution` - a
 * read of the WHOLE catalogue, including every competitor's supplier terms -
 * with one careless toggle, and the server honoured it. The server refuses it
 * now (`CONTENT-3`); this stops the form from asking.
 *
 * The mirror also matters: the two owner keys are hidden from staff roles,
 * because they authorise nothing without an owner scope and a switch that
 * cannot do anything teaches people the switches do not mean much.
 */
/**
 * Grouped and collapsed by default (`WL-9`).
 *
 * 🚨 Flat, it pushed the dialog's own buttons off screen the moment a role
 * offered more than one or two groups - "Create user" read as disabled when
 * it was really just scrolled out of view. An accordion per area keeps the
 * whole picker reachable at a glance; the count in each summary means
 * nothing has to be expanded just to see WHETHER anything in it is on.
 */
function PermissionPicker({
  value,
  onChange,
  disabled,
  role,
  scoped = false,
}: {
  value: PermissionKey[];
  onChange: (next: PermissionKey[]) => void;
  disabled?: boolean;
  role: SimpleRole;
  /** A customer's user (tied to a company or locations): no staff-only keys. */
  scoped?: boolean;
}) {
  const owner = role === "contentOwner";
  const offered = PERMISSION_CATALOGUE.filter((p) =>
    owner ? !p.adminOnly : !p.ownerOnly && !(scoped && p.adminOnly)
  );
  function toggle(key: PermissionKey) {
    if (value.includes(key)) onChange(value.filter((k) => k !== key));
    else onChange([...value, key]);
  }
  // Derive the rendered groups from the catalogue (preferred order first, any
  // others appended) so a newly added permission group - e.g. "Care" - shows up
  // automatically instead of being silently dropped by a hardcoded list.
  const GROUP_ORDER = ["Premise", "Hospitality", "Care", "Global"];
  const groups = Array.from(new Set(offered.map((p) => p.group))).sort(
    (a, b) => {
      const ia = GROUP_ORDER.indexOf(a);
      const ib = GROUP_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    }
  );
  return (
    <Box sx={{ opacity: disabled ? 0.6 : 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Permissions
      </Typography>
      {disabled && (
        <Typography variant="caption" color="text.secondary">
          Admins have every permission implicitly - no overrides needed.
        </Typography>
      )}
      <Stack sx={{ mt: 1 }}>
        {groups.map((g) => {
          const inGroup = offered.filter((p) => p.group === g);
          const selectedCount = inGroup.filter((p) =>
            value.includes(p.key)
          ).length;
          return (
            <Accordion
              key={g}
              disableGutters
              variant="outlined"
              square
              sx={{
                "&:first-of-type": {
                  borderTopLeftRadius: 4,
                  borderTopRightRadius: 4,
                },
                "&:last-of-type": {
                  borderBottomLeftRadius: 4,
                  borderBottomRightRadius: 4,
                },
                "&:not(:last-of-type)": { borderBottom: 0 },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon fontSize="small" />}
                data-testid={`perm-group-${g}`}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ width: "100%", pr: 1 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {g}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    {selectedCount === 0
                      ? "None"
                      : `${selectedCount} of ${inGroup.length}`}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Stack>
                  {inGroup.map((p) => (
                    <FormControlLabel
                      key={p.key}
                      control={
                        <Switch
                          size="small"
                          checked={value.includes(p.key)}
                          onChange={() => toggle(p.key)}
                          disabled={disabled}
                          inputProps={
                            {
                              "data-testid": `perm-${p.key}`,
                            } as React.InputHTMLAttributes<HTMLInputElement>
                          }
                        />
                      }
                      label={p.label}
                    />
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
    </Box>
  );
}

function AddUserDialog({
  open,
  companies,
  premises,
  owners,
  suppliers,
  suppliersUnavailable,
  onClose,
  onCreated,
  onError,
}: {
  open: boolean;
  companies: CompanySummary[];
  premises: PremiseSummary[];
  owners: ContentOwnerRecord[];
  /** The owner list could not be READ - not the same as empty. */
  suppliers: ContentSupplierRow[];
  /** The supplier list could not be READ - not the same as empty. */
  suppliersUnavailable: boolean;
  onClose: () => void;
  onCreated: (u: UserRecord) => void;
  onError: (msg: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<SimpleRole>("editor");
  const [company, setCompany] = useState<string>("");
  const [contentOwner, setContentOwner] = useState<string>("");
  const [extras, setExtras] = useState<string[]>([]);
  const [newLoginSlug, setNewLoginSlug] = useState("");
  const [newLoginContactEmail, setNewLoginContactEmail] = useState("");
  const [newLoginKind, setNewLoginKind] = useState<ContentOwnerKindValue | "">(
    ""
  );
  const [perms, setPerms] = useState<PermissionKey[]>(
    DEFAULT_PERMISSIONS_FOR_ROLE.editor
  );
  const [busy, setBusy] = useState(false);

  const [emailError, setEmailError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail("");
      setPassword("");
      setRole("editor");
      setCompany("");
      setContentOwner("");
      setExtras([]);
      setNewLoginSlug("");
      setNewLoginContactEmail("");
      setNewLoginKind("");
      setPerms(DEFAULT_PERMISSIONS_FOR_ROLE.editor);
      setEmailError(null);
    }
  }, [open]);

  function changeRole(next: SimpleRole) {
    setRole(next);
    setPerms(DEFAULT_PERMISSIONS_FOR_ROLE[next]);
  }

  async function submit() {
    if (!isEmail(email)) {
      setEmailError("Enter a valid email address.");
      return;
    }
    setBusy(true);
    setEmailError(null);
    if (role === "contentOwner") {
      try {
        await ensureOwnerLogin({
          supplierId: contentOwner,
          suppliers,
          slug: newLoginSlug,
          contactEmail: newLoginContactEmail,
          kind: newLoginKind,
        });
      } catch (e) {
        onError(ownerLoginErrorHint(e));
        setBusy(false);
        return;
      }
    }
    try {
      const stored =
        SIMPLE_ROLES.find((r) => r.key === role)?.storedRole ?? "companyAdmin";
      const created = await foyerApi.createUser({
        email,
        password,
        type: stored,
        permissions: grantablePermissions(perms, role, company, extras),
        // A content owner belongs to no company and holds no premise; sending
        // the other scope's fields anyway is what the server refuses.
        company: hasNoTenantScope(role) ? null : company || null,
        contentOwner: role === "contentOwner" ? contentOwner : null,
        premises: hasNoTenantScope(role) ? [] : extras,
      });
      onCreated(created);
    } catch (e) {
      if (errorCode(e) === "user_already_exists") {
        setEmailError(friendlyError(e));
      } else {
        onError(friendlyError(e));
      }
    } finally {
      setBusy(false);
    }
  }

  const passwordTooShort = password.length > 0 && password.length < 8;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add user</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Creates a user immediately with the password you set - the invite
            flow is skipped. Share the password with them out-of-band.
          </Typography>
          <TextField
            label="Email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError(null);
            }}
            size="small"
            required
            autoFocus
            error={!!emailError}
            helperText={emailError ?? undefined}
            inputProps={{ "data-testid": "user-add-email" }}
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            size="small"
            required
            error={passwordTooShort}
            helperText={
              passwordTooShort
                ? "At least 8 characters."
                : "Minimum 8 characters."
            }
            inputProps={{ "data-testid": "user-add-password" }}
          />
          <TextField
            select
            label="Role"
            value={role}
            onChange={(e) => changeRole(e.target.value as SimpleRole)}
            size="small"
          >
            {SIMPLE_ROLES.map((r) => (
              <MenuItem key={r.key} value={r.key}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>

          <ScopePicker
            companies={companies}
            premises={premises}
            owners={owners}
            suppliers={suppliers}
            suppliersUnavailable={suppliersUnavailable}
            company={company}
            contentOwner={contentOwner}
            extras={extras}
            newLoginSlug={newLoginSlug}
            newLoginContactEmail={newLoginContactEmail}
            newLoginKind={newLoginKind}
            role={role}
            onCompany={setCompany}
            onContentOwner={setContentOwner}
            onExtras={setExtras}
            onNewLoginSlug={setNewLoginSlug}
            onNewLoginContactEmail={setNewLoginContactEmail}
            onNewLoginKind={setNewLoginKind}
          />

          <PermissionPicker
            value={perms}
            onChange={setPerms}
            disabled={role === "admin"}
            role={role}
            scoped={isCustomerScope(role, company, extras)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={busy || !isEmail(email) || password.length < 8}
          onClick={submit}
          data-testid="user-add-submit"
        >
          {busy ? "Creating…" : "Create user"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
